import { Locale } from 'shared/i18n/interface'
import { byId } from '~/app/utils/byId'
import { getProductOptionsHash } from '~/app/utils/getProductOptionsHash'
import { buildFullyQualifiedVistaprintUrl } from '~/app/utils/vistaprintUrlBuilder'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { GalleryModelsUrl } from 'shared/types/gallery/models'
import type { GalleryProductProductOptionsGroupsByProductKey } from 'shared/types/gallery/product'
import type { State } from 'shared/types/gallery/state'

export type ContentState = {
  designs: State.DesignState
  productOptionsByProductKeyMap: GalleryProductProductOptionsGroupsByProductKey
  tileEntities: State.TileEntityState
}

interface BuildTileEntityOptions {
  tileEntity: GalleryContentQuery.TileEntityResponse
  index?: number
  designs: State.Design[]
  productOptionsByProductKeyMap: GalleryProductProductOptionsGroupsByProductKey
  isQSPEntity?: boolean
  locale: Locale
  quantity: GalleryModelsUrl.ValidParsedQsValue<number>
  getTaxonomyIdByCategoryId: (categoryId: string) => string
  selectedTaxonomyIds?: string[]
  isSimilarTemplate?: boolean
}

interface BuildContentStateOptions {
  content: GalleryContentQuery.TileEntityResponse[]
  locale: Locale
  quantity: GalleryModelsUrl.ValidParsedQsValue<number>
  getTaxonomyIdByCategoryId: (categoryId: string) => string
  qvContent?: GalleryContentQuery.TileEntityResponse
  selectedTaxonomyIds?: string[]
  oldState?: State.GlobalState
}

/**
 * Builds the `facets` state properties from the content result facets
 * @param {GalleryContentQuery.Facet[]} facets
 * @return {State.FacetState} Facet State
 */
export function buildFacetState(facets: GalleryContentQuery.Facet[]): { facets: State.FacetState } {
  // make sure facet is lower case
  facets.forEach((f) => {
    f.facet = f.facet.toLowerCase()
  })

  return {
    facets: {
      byId: byId<GalleryContentQuery.Facet>(facets, 'facet').byId,
    },
  }
}

function buildTaxonomyState(
  tileCategories: string[],
  selectedTaxonomyIds: string[],
  getParentByChildTaxonomyId: (taxonomyId: string) => string,
): Record<string, string> {
  const taxonomyVariants = tileCategories.reduce(
    (accum: Record<string, string>, v: string) => {
      const parent = getParentByChildTaxonomyId(v)

      if (parent) {
        return {
          ...accum,
          [parent]: v,
        }
      }

      return accum
    },
    {} as Record<string, string>,
  )

  for (const selectedTaxonomyId of selectedTaxonomyIds || []) {
    if (tileCategories.includes(selectedTaxonomyId)) {
      const parent = getParentByChildTaxonomyId(selectedTaxonomyId)

      if (parent) {
        taxonomyVariants[parent] = selectedTaxonomyId
      }
    }
  }

  return taxonomyVariants
}

export function buildTileEntity({
  tileEntity,
  index = 0,
  designs,
  productOptionsByProductKeyMap,
  isQSPEntity = false,
  locale,
  quantity,
  getTaxonomyIdByCategoryId,
  selectedTaxonomyIds,
  isSimilarTemplate,
}: BuildTileEntityOptions): State.TileEntity {
  let colorSwatchIds: string[] = []
  const { colorSwatches, fullProductOptions, productKey, productVersion, ...rest } = tileEntity

  rest.studioUrl = buildFullyQualifiedVistaprintUrl({
    path: rest.studioUrl,
    locale,
    quantity,
  })
  rest.nonPersonalizedStudioUrl = buildFullyQualifiedVistaprintUrl({
    path: rest.nonPersonalizedStudioUrl,
    locale,
    quantity,
  })

  if (colorSwatches?.length) {
    colorSwatchIds = colorSwatches.map((colorSwatch) => {
      const updatedColorSwatch = {
        ...colorSwatch,
        entityId: tileEntity.designId,
        studioUrl: buildFullyQualifiedVistaprintUrl({
          path: colorSwatch.studioUrl,
          locale,
          quantity,
        }),
        nonPersonalizedStudioUrl: buildFullyQualifiedVistaprintUrl({
          path: colorSwatch.nonPersonalizedStudioUrl,
          locale,
          quantity,
        }),
      }

      designs.push(updatedColorSwatch)
      return updatedColorSwatch.designId
    })
  } else {
    designs.push({
      ...rest,
      entityId: tileEntity.designId,
    })
  }

  // Each tileEntity maps to a single pricing option based on the
  // product options. Build the productOptionsHash to add to the tileEntity
  // state for lookup against the pricing state
  let productOptionsHash = ''

  if (fullProductOptions) {
    productOptionsHash = getProductOptionsHash(fullProductOptions)

    // Build up the productOptionsByProductKeyMap state
    // productOptionsByProductKeyMap is a linking table between tileEntities and other content
    // that requires associating tileEntitie's productOptions to data (i.e. pricing)
    if (!(productKey in productOptionsByProductKeyMap)) {
      productOptionsByProductKeyMap[productKey] = {}
    }

    // Build up the productOptionsByProductKeyMap state
    // productOptionsByProductKeyMap is a linking table between tileEntities and other content
    // that requires associating tileEntitie's productOptions to data (i.e. pricing)
    if (!(productKey in productOptionsByProductKeyMap)) {
      productOptionsByProductKeyMap[productKey] = {}
    }
  }

  productOptionsByProductKeyMap[productKey][productOptionsHash] = fullProductOptions
  return {
    ...rest,
    productKey,
    productVersion: +productVersion,
    productOptionsHash,
    fullProductOptions,
    colorSwatches: colorSwatchIds,
    isQSPEntity,
    position: index,
    taxonomyVariants: buildTaxonomyState(tileEntity?.categoryKeys || [], selectedTaxonomyIds || [], getTaxonomyIdByCategoryId),
    colorComposition: tileEntity.colorComposition,
    isSimilarTemplate,
    productOptionsByProductKey: productOptionsByProductKeyMap,
  }
}

/**
 * Builds the "content" state.
 * The "content" state actually encompasses two states in our store:
 * `designs` and `tileEntities`. As both of those states rely
 * on data from the content query, this function filters through the content
 * result and returns both states as a store fragment.
 */
export function buildContentState({
  content,
  locale,
  quantity,
  getTaxonomyIdByCategoryId,
  qvContent,
  selectedTaxonomyIds,
  oldState,
}: BuildContentStateOptions): ContentState {
  const designs: State.Design[] = []
  const productOptionsByProductKeyMap: GalleryProductProductOptionsGroupsByProductKey = {}

  const tileEntities = content.map(
    (tileEntity, index): State.TileEntity =>
      buildTileEntity({
        tileEntity,
        index,
        designs,
        productOptionsByProductKeyMap,
        locale,
        quantity,
        getTaxonomyIdByCategoryId,
        selectedTaxonomyIds,
      }),
  )

  const designsById = byId<State.Design>(designs, 'designId', oldState?.designs)
  const tileEntitiesById = byId<State.TileEntity>(tileEntities, 'designId', oldState?.tileEntities)

  if (qvContent) {
    const qvDesigns: State.Design[] = []
    const qvTileEntity = buildTileEntity({
      tileEntity: qvContent,
      designs: qvDesigns,
      productOptionsByProductKeyMap,
      isQSPEntity: true,
      locale,
      quantity,
      getTaxonomyIdByCategoryId,
      selectedTaxonomyIds,
    })

    qvDesigns.forEach((qvDesign) => (designsById.byId[qvDesign.designId] = qvDesign))
    tileEntitiesById.byId[qvTileEntity.designId] = {
      ...qvTileEntity,
      position: tileEntitiesById.byId[qvTileEntity.designId]?.position ?? qvTileEntity.position,
    }
  }
  return {
    productOptionsByProductKeyMap,
    designs: designsById,
    tileEntities: tileEntitiesById,
  }
}
